import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TableModule } from "primeng/table";
import { CardModule } from "primeng/card";
import { InputTextModule } from "primeng/inputtext";
import { MessageModule } from "primeng/message";
import { BuyerDto } from "../../../core/models/buyer/buyer.dto";
import { BuyerListingService } from "../../../core/services/buyer-listing.service";
import { SpinnerComponent } from "../../../shared/components/spinner/spinner.component";

/** Client Portal Buyer-role users available to the signed-in sales user. */
@Component({
  selector: "jni-buyer",
  imports: [
    TableModule,
    CardModule,
    InputTextModule,
    MessageModule,
    ReactiveFormsModule,
    SpinnerComponent,
  ],
  templateUrl: "./buyer.component.html",
  styleUrl: "./buyer.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuyerComponent implements OnInit {
  private readonly _buyerService = inject(BuyerListingService);
  private readonly _destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly hasLoaded = signal(false);
  readonly loadError = signal(false);
  readonly buyers = signal<BuyerDto[]>([]);
  readonly defaultRows = 25;

  readonly filterControls = {
    clientId: new FormControl("", { nonNullable: true }),
    clientName: new FormControl("", { nonNullable: true }),
    buyerName: new FormControl("", { nonNullable: true }),
    email: new FormControl("", { nonNullable: true }),
    sampleAddress: new FormControl("", { nonNullable: true }),
    sampleSize: new FormControl("", { nonNullable: true }),
  };

  readonly columns = [
    {
      field: "clientId",
      label: "Client ID",
      filterControl: this.filterControls.clientId,
    },
    {
      field: "clientName",
      label: "Client Name",
      filterControl: this.filterControls.clientName,
    },
    {
      field: "buyerName",
      label: "Buyer Name",
      filterControl: this.filterControls.buyerName,
    },
    {
      field: "email",
      label: "Email",
      filterControl: this.filterControls.email,
    },
    {
      field: "sampleAddress",
      label: "Sample Address",
      filterControl: this.filterControls.sampleAddress,
    },
    {
      field: "sampleSize",
      label: "Sample Size",
      filterControl: this.filterControls.sampleSize,
    },
  ];

  ngOnInit(): void {
    this._buyerService
      .getBuyers()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: res => {
          this.buyers.set(res.data ?? []);
          this.loading.set(false);
          this.hasLoaded.set(true);
        },
        error: () => {
          this.buyers.set([]);
          this.loadError.set(true);
          this.loading.set(false);
          this.hasLoaded.set(true);
        },
      });
  }
}
